import React, { useState, useEffect, useMemo } from 'react'
import {
  Typography, Box, Button, Alert, CircularProgress, Stack,
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { gql, useQuery, useMutation } from '@apollo/client'
import PayoutConfigForm from '../components/PayoutConfig/PayoutConfigForm'
import EntityPicker from '../components/PayoutConfig/EntityPicker'
import {
  getRiderPayoutOverride,
  saveRiderPayoutOverride,
  clearRiderPayoutOverride,
} from '../apollo/mutations'
import { getRiders } from '../apollo/queries'
import { driverPayTheme, DP, focusRing } from './driverPayTheme'

const GET = gql`${getRiderPayoutOverride}`
const SAVE = gql`${saveRiderPayoutOverride}`
const CLEAR = gql`${clearRiderPayoutOverride}`
const RIDERS = gql`${getRiders}`

const NUM_FIELDS = [
  'opt1HourlyRate', 'opt2HourlyRate', 'opt2PerOrderBonus',
  'opt3FlatFeePerOrder', 'opt4BaseFee', 'opt4PerMileRate',
  'opt5PercentOfDeliveryFee', 'opt6MinimumPerDelivery', 'opt8BonusPerExtraOrder',
]
const BOOL_FIELDS = [
  'opt1Enabled', 'opt2Enabled', 'opt3Enabled', 'opt4Enabled',
  'opt5Enabled', 'opt6Enabled', 'opt8Enabled',
]
// ─── Presentation ───
const PAGE = {
  minHeight: { xs: 'calc(100vh - 80px)', sm: '100vh' }, bgcolor: DP.page, color: DP.text,
  fontFamily: DP.font, fontSize: 15, lineHeight: 1.45
}
const content = maxWidth => ({
  maxWidth, mx: 'auto', boxSizing: 'content-box',
  px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 4.5 }, pb: { xs: 5, md: 7 },
  display: 'flex', flexDirection: 'column', gap: 3
})
const PAGE_TITLE = { fontWeight: 800, fontSize: { xs: 26, sm: 30 }, letterSpacing: '-0.02em', lineHeight: 1.2, color: DP.text }
const PAGE_DESC = { fontSize: 15, lineHeight: 1.5, color: DP.body, mt: '6px' }
// The dark band behind the driver picker: light label, 52px white field.
const PICKER_BAND = {
  bgcolor: DP.text, borderRadius: '14px', p: '20px',
  '& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.Mui-disabled': { color: DP.bannerSub },
  '& .MuiAutocomplete-root .MuiOutlinedInput-root, & .MuiAutocomplete-root .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
    minHeight: 52, bgcolor: '#FFFFFF'
  },
  '& .MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input': { height: 40, fontSize: 17, fontWeight: 600 },
  '& .MuiOutlinedInput-root.Mui-focused': focusRing,
  '& .MuiOutlinedInput-root.Mui-disabled': { bgcolor: '#E7EAEF' }
}

function pickInput(cfg) {
  const out = {}
  BOOL_FIELDS.forEach(k => { if (cfg?.[k] != null) out[k] = !!cfg[k] })
  NUM_FIELDS.forEach(k => {
    if (cfg?.[k] != null && cfg[k] !== '') {
      const n = parseFloat(cfg[k])
      if (Number.isFinite(n)) out[k] = n
    }
  })
  return out
}

export default function MyDriverOverride() {
  const restaurantId = localStorage.getItem('restaurantId')

  const { data: ridersData, loading: ridersLoading } = useQuery(RIDERS, {
    fetchPolicy: 'cache-and-network',
  })
  // Restaurant panel: only show staff drivers of this restaurant.
  const myStaffDrivers = useMemo(() => {
    const all = ridersData?.riders || []
    return all.filter(r =>
      r.driverType === 'RESTAURANT' &&
      String(r.restaurantId) === String(restaurantId)
    )
  }, [ridersData, restaurantId])

  const [riderId, setRiderId] = useState('')
  const { data, loading, error, refetch } = useQuery(GET, {
    variables: { riderId },
    skip: !riderId,
    fetchPolicy: 'cache-and-network',
  })

  const [cfg, setCfg] = useState({})
  const [saved, setSaved] = useState('')
  const [save, { loading: saving, error: saveError }] = useMutation(SAVE, {
    onCompleted: () => { setSaved('Override saved.'); refetch() },
  })
  const [clearMut, { loading: clearing }] = useMutation(CLEAR, {
    onCompleted: () => { setSaved('Override cleared.'); setCfg({}); refetch() },
  })

  useEffect(() => {
    if (data?.riderPayoutOverride) setCfg(data.riderPayoutOverride)
    else if (data) setCfg({})
  }, [data])

  if (!restaurantId) {
    return (
      <ThemeProvider theme={driverPayTheme}>
        <Box sx={PAGE}>
          <Box sx={content(860)}>
            <Alert severity="error">No restaurant context. Please re-login.</Alert>
          </Box>
        </Box>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={driverPayTheme}>
      <Box sx={PAGE}>
        <Box sx={content(860)}>
        <Box component="header">
        <Typography variant="h5" component="h1" sx={PAGE_TITLE}>
          Per-Driver Pay Override
        </Typography>
        <Typography variant="body2" sx={PAGE_DESC}>
          Pay a specific staff driver a different rate than your restaurant default — useful for
          experienced drivers, performance bonuses, or trial periods. Any option set here wins
          over your restaurant-wide Driver Payouts config; unset options fall back.
        </Typography>
        </Box>

        <Box sx={PICKER_BAND}>
          <EntityPicker
            appearance="ink"
            options={myStaffDrivers}
            value={riderId}
            onChange={setRiderId}
            loading={ridersLoading}
            label="Driver"
            placeholder={myStaffDrivers.length ? 'Pick one of your staff drivers' : 'No staff drivers yet'}
            getLabel={(o) => o?.name || 'Unnamed'}
            getSubLabel={(o) => o?.username || o?.phone || `Driver #${o?._id}`}
            disabled={myStaffDrivers.length === 0}
          />
        </Box>

        {riderId && loading && !data && <CircularProgress />}
        {error && <Alert severity="error">{error.message}</Alert>}
        {saveError && <Alert severity="error">{saveError.message}</Alert>}
        {saved && <Alert severity="success" onClose={() => setSaved('')}>{saved}</Alert>}

        {riderId && (
          <>
            <PayoutConfigForm value={cfg} onChange={setCfg} disabled={saving || clearing} appearance="ink" />
            <Stack direction="row" sx={{ pt: 2.5, borderTop: `1px solid ${DP.border}`, justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1.25 }}>
              <Button
                variant="outlined"
                color="error"
                disabled={saving || clearing}
                onClick={() => { setSaved(''); clearMut({ variables: { riderId } }) }}
                sx={{ textTransform: 'capitalize' }}
              >
                {clearing ? 'Clearing…' : 'Clear override'}
              </Button>
              <Button
                variant="contained"
                disabled={saving || clearing}
                onClick={() => {
                  setSaved('')
                  save({ variables: { riderId, input: pickInput(cfg) } })
                }}
                sx={{ textTransform: 'capitalize' }}
              >
                {saving ? 'Saving…' : 'Save override'}
              </Button>
            </Stack>
          </>
        )}
        </Box>
      </Box>
    </ThemeProvider>
  )
}
